import type { Tip } from '@copilot/shared';
import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { meetingsApi } from '../../api/meetings';
import { Badge, Button, Card, EmptyState, ErrorBanner, Input, Loading } from '../../components';
import { cn } from '../../lib/cn';
import { formatOffset } from '../../lib/format';
import { buildTimeline, indexMatches, splitMatches, stepMatch, type TimelineItem } from '../../lib/transcript';
import { TipTypeBadge } from './badges';

/**
 * Transcript with tips interleaved by call offset. The transcript is only
 * fetched after an explicit click because the server audit-logs every view.
 */
export function TranscriptViewer({ meetingId, tips, available }: { meetingId: string; tips: Tip[]; available: boolean }) {
  const [shown, setShown] = useState(false);
  const query = useQuery({
    queryKey: ['transcript', meetingId],
    queryFn: () => meetingsApi.transcript(meetingId),
    enabled: shown && available,
    staleTime: Infinity,
  });

  if (!available) {
    return (
      <Card title="Transcript">
        <p className="mb-3 text-sm text-muted">
          No transcript is stored for this meeting (not recorded, or removed by the retention policy).
        </p>
        {tips.length > 0 && <Timeline items={buildTimeline([], tips)} query="" activeMatch={-1} firstIndexBySegment={new Map()} />}
      </Card>
    );
  }

  if (!shown) {
    return (
      <Card title="Transcript">
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => setShown(true)}>Show transcript</Button>
          <span className="text-xs text-muted">
            Opening the transcript is recorded in the audit log.
            {tips.length > 0 && ` ${tips.length} tip${tips.length === 1 ? '' : 's'} shown during the call appear on the timeline.`}
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Transcript" description="This view was recorded in the audit log.">
      {query.isLoading && <Loading label="Loading transcript…" />}
      {query.isError && <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />}
      {query.data && <SearchableTimeline items={buildTimeline(query.data, tips)} />}
    </Card>
  );
}

function SearchableTimeline({ items }: { items: TimelineItem[] }) {
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search);
  const { total, firstIndexBySegment } = useMemo(() => indexMatches(items, query), [items, query]);
  const [active, setActive] = useState(-1);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => setActive(total > 0 ? 0 : -1), [query, total]);
  useEffect(() => {
    if (active < 0) return;
    scroller.current?.querySelector(`[data-match="${active}"]`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [active, query]);

  const step = (delta: 1 | -1) => setActive((a) => stepMatch(a, total, delta));
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      step(e.shiftKey ? -1 : 1);
    }
  };

  if (items.length === 0) return <EmptyState title="The transcript is empty" />;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search transcript…"
          aria-label="Search transcript"
          className="max-w-xs"
        />
        <span className="min-w-20 text-xs text-muted tabular-nums" aria-live="polite">
          {query.trim() ? (total ? `${active + 1} of ${total}` : 'No matches') : ''}
        </span>
        <Button size="sm" disabled={!total} onClick={() => step(-1)} aria-label="Previous match">
          ↑
        </Button>
        <Button size="sm" disabled={!total} onClick={() => step(1)} aria-label="Next match">
          ↓
        </Button>
      </div>
      <div ref={scroller} className="max-h-[70vh] overflow-y-auto pr-1">
        <Timeline items={items} query={query} activeMatch={active} firstIndexBySegment={firstIndexBySegment} />
      </div>
    </div>
  );
}

function Timeline({
  items,
  query,
  activeMatch,
  firstIndexBySegment,
}: {
  items: TimelineItem[];
  query: string;
  activeMatch: number;
  firstIndexBySegment: Map<string, number>;
}) {
  return (
    <ol className="space-y-1.5">
      {items.map((item) =>
        item.kind === 'tip' ? (
          <li key={item.key}>
            <TipCard tip={item.tip} />
          </li>
        ) : (
          <li key={item.key} className="grid grid-cols-[3.75rem_3.25rem_1fr] items-baseline gap-2 text-sm">
            <span className="font-mono text-xs text-muted tabular-nums">{formatOffset(item.segment.startMs)}</span>
            <span>
              <Badge tone={item.segment.source === 'me' ? 'accent' : 'neutral'}>{item.segment.source === 'me' ? 'Me' : 'Them'}</Badge>
            </span>
            <p className={cn('leading-relaxed', item.segment.source === 'me' ? 'text-ink' : 'text-ink-2')}>
              {splitMatches(item.segment.text, query, firstIndexBySegment.get(item.segment.id) ?? 0).map((part, i) =>
                part.matchIndex === undefined ? (
                  <span key={i}>{part.text}</span>
                ) : (
                  <mark
                    key={i}
                    data-match={part.matchIndex}
                    className={cn('rounded-sm px-0.5 text-ink', part.matchIndex === activeMatch ? 'bg-highlight-active' : 'bg-highlight')}
                  >
                    {part.text}
                  </mark>
                ),
              )}
            </p>
          </li>
        ),
      )}
    </ol>
  );
}

export function TipCard({ tip }: { tip: Tip }) {
  return (
    <div className="my-2 ml-[7.5rem] rounded-md border border-accent/30 border-l-4 border-l-accent bg-accent-soft/50 px-3 py-2 text-sm max-sm:ml-0">
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono text-muted tabular-nums">{formatOffset(tip.callOffsetMs)}</span>
        <span className="font-medium text-ink-2">Tip shown</span>
        <TipTypeBadge type={tip.type} />
        {tip.useful === true && <span title="Rep marked this tip as useful">👍 Useful</span>}
        {tip.useful === false && <span title="Rep marked this tip as not useful">👎 Not useful</span>}
      </div>
      {tip.term && <div className="font-semibold text-ink">{tip.term}</div>}
      <p className="whitespace-pre-line text-ink">{tip.text}</p>
    </div>
  );
}
